import { alpha } from '@mui/material'
import React from 'react'

import { DownIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/DownIcon'

export const COLOR = {
  primary: '#3B4768',
  secondary: '#7feb89',
  secondary100: '#2bd0a4',
  tertiary: '#1f64af',
  active: '#3D79B6',
  error: '#FFA366',
  errorDark: '#f53158',
  neutral25: '#f9f9fa',
  neutral50: '#d2d4d9',
  neutral100: '#b6b9c0',
  neutral200: '#7e8491',
  neutral400: '#5e6371',
  background10: '#fbfcfe',
  background15: '#f7fafc',
  background25: '#F3F7FB'
}

export const theme = {
  palette: {
    primary: {
      main: COLOR.primary,
      active: COLOR.active,
      light: alpha(COLOR.primary, 0.7)
    },
    secondary: {
      main: COLOR.secondary,
      light: COLOR.secondary100,
      contrastText: COLOR.primary
    },
    error: {
      main: COLOR.error,
      dark: COLOR.errorDark
    },
    text: { primary: COLOR.tertiary, active: COLOR.active },
    grey: { 50: COLOR.neutral50 },
    glass: {
      100: COLOR.background10,
      300: COLOR.background15,
      500: COLOR.background25
    },
    stone: {
      50: COLOR.neutral50,
      100: COLOR.neutral100,
      300: COLOR.neutral200,
      500: COLOR.neutral400
    }
  },
  typography: {
    fontFamily: ['Gilroy', 'Lato'].join(','),
    fontSize: 14,
    fontWeightMedium: 600,
    h1: { fontSize: 32 },
    h2: {
      fontSize: 28,
      '@media (min-width:900px)': {
        fontSize: 32
      }
    },
    h3: { fontSize: 24 },
    h4: {
      fontSize: 16,
      '@media (min-width:900px)': {
        fontSize: 18
      }
    },
    allVariants: { color: COLOR.primary },
    h5: {
      fontFamily: 'Gilroy',
      fontSize: 24,
      fontWeight: 700,
      lineHeight: 1.33,
      marginBottom: '16px'
    },
    subtitle1: { fontFamily: 'Lato', fontSize: 18 },
    body1: { fontFamily: 'Lato', fontSize: 16 },
    body2: {
      fontFamily: 'Lato',
      fontSize: 14,
      lineHeight: 1.65
    },
    caption: { fontFamily: 'Lato', fontSize: 12 },
    sectionTitle: {
      fontFamily: 'Gilroy',
      fontSize: 28,
      '@media (min-width:900px)': {
        fontSize: 32
      }
    },
    mediumTitle: {
      fontFamily: 'Gilroy',
      fontSize: 28
    },
    minorTitle: {
      fontFamily: 'Gilroy',
      fontSize: 24
    },
    bodyTitle: {
      fontFamily: 'Gilroy',
      fontSize: 16,
      '@media (min-width:900px)': {
        fontSize: 18
      }
    }
  },
  font: {
    base: 'Lato',
    fancy: 'Gilroy'
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          sectionTitle: 'h2',
          mediumTitle: 'h2',
          minorTitle: 'h3',
          bodyTitle: 'h4'
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: 'Lato',
          backgroundColor: COLOR.primary,
          fontSize: 14,
          padding: 16,
          fontWeight: 400,
          minWidth: 220
        },
        // @TODO arrow left and popper inset force the margin between the tooltip and the right edge of the body tag. not ideal solution, to be improved
        arrow: { color: COLOR.primary, left: '16px !important' },
        popper: {
          inset: '0px auto auto -16px !important'
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: COLOR.background25,
          boxShadow: 'none',
          borderRadius: 'var(--border-radius-card-medium)',
          '&:last-of-type': {
            borderRadius: 'var(--border-radius-card-medium)'
          },
          '&::before': { display: 'none' }
        }
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        content: { margin: '16px 0', '&.Mui-expanded': { margin: '16px 0' } }
      },
      defaultProps: { expandIcon: <DownIcon /> }
    },
    MuiAccordionDetails: {
      styleOverrides: { root: { padding: '0 16px 24px 16px' } }
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          '&:focus-visible': { outline: `2px solid ${COLOR.primary}` }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&': {
            borderRadius: 'var(--border-radius-cta)'
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          '&:-webkit-autofill': {
            WebkitTextFillColor: 'black',
            WebkitBoxShadow: '0 0 0 30px white inset !important'
          },
          color: '#000'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: COLOR.primary
        }
      }
    }
  }
}
